import { useState } from "react";
import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import SearchBand from "@/pages/home/components/SearchBand";
import CategorySidebar from "@/components/feature/CategorySidebar";
import SectionHeading from "@/components/base/SectionHeading";
import Pagination from "@/components/base/Pagination";
import { NewsListCard, ResourceCard, TopicRow } from "@/components/feature/ContentCards";
import { newsList, resourceList, siteStats, topicList } from "@/mocks/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const NEWS_PAGE_SIZE = 3;

export default function Home() {
  const [newsPage, setNewsPage] = useState(1);

  useDocumentMeta({
    title: "18禁-成人内容资讯及资源平台",
    description:
      "18禁为您提供成人内容相关资讯、话题讨论以及资源介绍，涵盖影视、游戏及漫画等多个领域，满足成年用户的相关需求。",
    keywords: "18禁,成人内容,影视资源,游戏资讯,漫画推荐,成年专区,18禁话题",
  });

  const totalNewsPages = Math.ceil(newsList.length / NEWS_PAGE_SIZE);
  const currentNews = newsList.slice((newsPage - 1) * NEWS_PAGE_SIZE, newsPage * NEWS_PAGE_SIZE);
  const featuredResources = resourceList.slice(0, 6);
  const hotTopics = topicList.slice(0, 4);

  return (
    <div>
      <HeroCarousel />
      <SearchBand />

      <div className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-14">
        <div className="grid gap-8 lg:grid-cols-[248px_1fr] lg:gap-10">
          <aside className="lg:sticky lg:top-[118px] lg:self-start">
            <CategorySidebar />
          </aside>

          <div className="min-w-0 space-y-14">
            <section id="news">
              <SectionHeading
                eyebrow="LATEST STORIES"
                title="资讯精选"
                description="影视、游戏、漫画与行业观察，每天更新的成年向内容报道。"
                action={
                  <Link
                    to="/news"
                    className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm text-foreground-600 transition hover:text-primary-700"
                  >
                    全部资讯
                    <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
                  </Link>
                }
              />
              <div className="space-y-3">
                {currentNews.map((item) => (
                  <NewsListCard key={item.id} item={item} />
                ))}
              </div>
              <div className="mt-6">
                <Pagination page={newsPage} totalPages={totalNewsPages} onChange={setNewsPage} />
              </div>
            </section>

            <section id="resources">
              <SectionHeading
                eyebrow="PICKS OF THE WEEK"
                title="资源推荐"
                description="近期上线与评分较高的影视、游戏、漫画条目，点击查看完整介绍。"
                action={
                  <Link
                    to="/resources"
                    className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm text-foreground-600 transition hover:text-primary-700"
                  >
                    进入资源库
                    <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
                  </Link>
                }
              />
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-5">
                {featuredResources.map((item) => (
                  <ResourceCard key={item.id} item={item} />
                ))}
              </div>
            </section>

            <section id="topics">
              <SectionHeading
                eyebrow="IN DISCUSSION"
                title="热门话题"
                description="读者正在讨论的成年向内容话题，欢迎理性交流。"
                action={
                  <Link
                    to="/topics"
                    className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm text-foreground-600 transition hover:text-primary-700"
                  >
                    全部话题
                    <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
                  </Link>
                }
              />
              <div className="space-y-3">
                {hotTopics.map((item, index) => (
                  <TopicRow key={item.id} item={item} index={index} />
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>

      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto grid max-w-[1280px] gap-10 px-4 py-14 md:px-6 md:py-16 lg:grid-cols-[1.2fr_1fr] lg:gap-16">
          <div>
            <div className="mb-4 flex items-center gap-2">
              <span className="h-px w-6 bg-accent-500"></span>
              <span className="text-[11px] font-medium tracking-[0.18em] text-accent-700">ABOUT THE ARCHIVE</span>
            </div>
            <h2 className="font-heading text-2xl leading-snug text-foreground-950 md:text-[30px]">
              把散落的成人内容信息，整理成可以阅读的样子
            </h2>
            <p className="mt-5 text-sm leading-[1.95] text-foreground-700">
              18禁是一个面向成年观众的资讯与资源整理平台。我们把成人影视、游戏、漫画的报道、条目信息与讨论集中在一处，用清晰的分级标注与筛选方式，让每一次浏览都更有目的性。
            </p>
            <p className="mt-4 text-sm leading-[1.95] text-foreground-600">
              站内的作品、评分、讨论与图片来源均为虚构演示内容，用于展示产品结构。我们不提供任何真实资源下载、播放或外链跳转。
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to="/about"
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-700"
              >
                了解我们
              </Link>
              <Link
                to="/contact"
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition hover:border-primary-300 hover:text-primary-700"
              >
                内容建议与反馈
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 self-center sm:gap-4">
            {siteStats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-lg border border-background-200 bg-background-50 px-5 py-6"
              >
                <p className="font-heading text-2xl text-primary-700 md:text-[28px]">{stat.value}</p>
                <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
                <span className="mt-3 block h-px w-8 bg-accent-400"></span>
                <p className="mt-2 text-[10px] tracking-[0.16em] text-foreground-400">DEMO DATA</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}